import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface UseCountdownTimerOptions {
  /** 카운트다운 총 길이(밀리초) */
  durationMs: number;
  /** 만료 절대 시각(Unix ms). 없으면 현재 시각 + durationMs를 사용 */
  deadlineTimestampMs?: number | null;
  /** 남은 시간 계산 주기(밀리초) */
  intervalMs?: number;
  /** 타이머 실행 여부. false면 타이머를 중지하고 초기 상태로 되돌림 */
  isRunning?: boolean;
  /** 남은 시간이 0 이하가 되는 시점에 1회 호출되는 콜백 */
  onExpire?: () => void;
  /** 값이 바뀌면 타이머를 재초기화하기 위한 외부 트리거 키 */
  resetKey?: string | number | null;
}

interface UseCountdownTimerReturn {
  isExpired: boolean;
  remainingMilliseconds: number;
  remainingSeconds: number;
}

const DEFAULT_INTERVAL_MS = 500;

const useCountdownTimer = ({
  durationMs,
  deadlineTimestampMs,
  isRunning = true,
  onExpire,
  resetKey,
  intervalMs = DEFAULT_INTERVAL_MS,
}: UseCountdownTimerOptions): UseCountdownTimerReturn => {
  const [remainingMilliseconds, setRemainingMilliseconds] = useState(durationMs);
  const deadlineRef = useRef<number | null>(null);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);

  useLayoutEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  const syncRemaining = useCallback(() => {
    if (deadlineRef.current == null) {
      setRemainingMilliseconds(durationMs);
      return;
    }

    const nextRemaining = Math.max(0, deadlineRef.current - Date.now());
    setRemainingMilliseconds(nextRemaining);

    if (nextRemaining <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpireRef.current?.();
    }
  }, [durationMs]);

  useEffect(() => {
    if (!isRunning) {
      deadlineRef.current = null;
      expiredRef.current = false;
      setRemainingMilliseconds(durationMs);
      return;
    }

    const fallbackDeadline = Date.now() + durationMs;
    deadlineRef.current = deadlineTimestampMs ?? fallbackDeadline;
    expiredRef.current = false;
    syncRemaining();
  }, [deadlineTimestampMs, durationMs, isRunning, resetKey, syncRemaining]);

  useEffect(() => {
    if (!isRunning || deadlineRef.current == null) {
      return;
    }

    syncRemaining();

    const intervalId = window.setInterval(syncRemaining, intervalMs);
    const handleVisibilityChange = () => {
      if (typeof document === 'undefined') {
        return;
      }

      if (document.visibilityState === 'visible') {
        syncRemaining();
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      window.clearInterval(intervalId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [intervalMs, isRunning, syncRemaining]);

  const remainingSeconds = useMemo(
    () => Math.max(0, Math.ceil(remainingMilliseconds / 1000)),
    [remainingMilliseconds],
  );

  return {
    remainingMilliseconds,
    remainingSeconds,
    isExpired: remainingMilliseconds <= 0,
  };
};

export default useCountdownTimer;
